import React, { useState, useEffect } from "react";
import Row from "../card/card"; 
import Dropdown from "../dropdown/dropdown"; 
import "./cardlist.css";
import Switch from '@mui/material/Switch';

export default function CardList({ teams, fixturesData, activeGameweek: initialActiveGameweek}) {
  const [numberOfGameweeks, setNumberOfGameweeks] = useState(5);
  const [sortOrder, setSortOrder] = useState("asc");
  const [sortBy, setSortBy] = useState("custom");
  const [sortColumn, setSortColumn] = useState("fdr"); // 'team', 'fdr', or 'gw-N'
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 800); 
  const [showOriginalScore] = useState(true);
  const [showAttack, setShowAttack] = useState(true);
  const [showDefense, setShowDefense] = useState(true);
  const [activeGameweek, setActiveGameweek] = useState(initialActiveGameweek || 1);

  // Update activeGameweek when the prop changes
  useEffect(() => {
    setActiveGameweek(initialActiveGameweek);
  }, [initialActiveGameweek]);

  // Listen for window resize to update mobile state
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 800);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Determine which difficulty property to use based on ATT/DEF filter
  // diffMode: 'overall' (both or neither), 'attack' (ATT only), 'defense' (DEF only)
  const diffMode = (showAttack && showDefense) || (!showAttack && !showDefense)
    ? 'overall'
    : showAttack ? 'attack' : 'defense';

  const getTeamDiff = (opponentNumber, isHome) => {
    const opponent = teams[opponentNumber];
    if (!opponent) return 0;
    if (diffMode === 'attack') {
      // ATT filter: how easy to score against opponent → opponent's defense rating
      // Low defense = weak defense = easy to score against
      return isHome ? (opponent.a_def || 5) : (opponent.h_def || 5);
    } else if (diffMode === 'defense') {
      // DEF filter: how dangerous opponent's attack is → opponent's attack rating
      // Low attack = weak attack = easy clean sheet
      return isHome ? (opponent.a_att || 5) : (opponent.h_att || 5);
    }
    // Overall
    return isHome ? (opponent.a_diff || 0) : (opponent.h_diff || 0);
  };

  const calculateTotalDifficulty = (teamId, numberOfFixtures) => {
    if (!fixturesData) return 0;

    const teamFixtures = fixturesData.filter(
      (fixture) => fixture.team_h === teamId || fixture.team_a === teamId
    );

    const gameweekFixtures = new Array(numberOfFixtures)
      .fill(null)
      .map(() => []);

    let totalDifficulty = 0; // Initialize totalDifficulty for the team

    for (let i = activeGameweek - 1; i < Math.min(activeGameweek + numberOfGameweeks - 1, 38); i++) {
      const gameweek = teamFixtures.filter(
        (fixture) => fixture.event === i + 1
      );

      const gameweekDifficulty = gameweek.reduce((acc, fixture) => {
        const home = fixture.team_h === teamId;
        const difficulty = home
          ? fixture.team_h_difficulty
          : fixture.team_a_difficulty;
        return acc + difficulty;
      }, 0);

      if (gameweek.length > 0) {
        gameweekFixtures[i - (activeGameweek - 1)] = gameweek.map((fixture) => {
          const opponentNumber =
            fixture.team_h === teamId ? fixture.team_a : fixture.team_h;
          const opponent = teams
            ? fixture.team_h === teamId
              ? teams[fixture.team_a].name
              : teams[fixture.team_h].name
            : "";

          const home = fixture.team_h === teamId;
          const difficulty = home
            ? fixture.team_h_difficulty
            : fixture.team_a_difficulty;

          return {
            opponent,
            opponentNumber,
            home,
            difficulty,
            eventNumber: fixture.event,
          };
        });
      } else {
        gameweekFixtures[i - (activeGameweek - 1)].push({
          opponent: "BLANK",
          opponentNumber: 0,
          difficulty: 6,
          eventNumber: i + 1,
        });
          
        // Add 6 for each blank gameweek
        totalDifficulty += 6;
      }

      totalDifficulty += gameweekDifficulty;
    }

    return totalDifficulty;
  };

  const calculateCustomDifficulty = (teamId, numberOfFixtures) => {
    if (!fixturesData) return 0;

    const teamFixtures = fixturesData.filter(
      (fixture) => fixture.team_h === teamId || fixture.team_a === teamId
    );

    let customTotalDifficulty = 0;

    for (let i = activeGameweek - 1; i < Math.min(activeGameweek + numberOfGameweeks - 1, 38); i++) {
      const gameweek = teamFixtures.filter(
        (fixture) => fixture.event === i + 1
      );

      if (gameweek.length === 0) {
        // Add 11 for each blank gameweek
        customTotalDifficulty += 11;
        continue;
      }

      customTotalDifficulty += gameweek.reduce((acc, fixture) => {
        const home = fixture.team_h === teamId;
        const opponentNumber = home ? fixture.team_a : fixture.team_h;
        const difficulty = getTeamDiff(opponentNumber, home);

        return acc + difficulty;
      }, 0);
    }

    return customTotalDifficulty;
  };

  const handleGameweekChange = (event) => {
    const newNumberOfGameweeks = parseInt(event.target.value, 10);
    setNumberOfGameweeks(newNumberOfGameweeks);
  };

  const handleCustomGameweekChange = (event) => {
    const newCustomActiveGameweek = parseInt(event.target.value, 10);
    setActiveGameweek(newCustomActiveGameweek);
  };
  
  
  const handleTableReorder = () => {
    setSortBy((prevSortBy) => (prevSortBy === "original" ? "custom" : "original"));
  };

  const handleColumnSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  const sortArrow = (column) => {
    const isActive = sortColumn === column;
    const icon = isActive ? (sortOrder === 'asc' ? '▲' : '▼') : '↕';
    return <span className={`sort-icon ${isActive ? 'active' : ''}`}>{icon}</span>;
  };

  const getGameweekDifficulty = (teamId, gwNumber) => {
    if (!fixturesData) return 99;
    const teamFixtures = fixturesData.filter(
      f => (f.team_h === teamId || f.team_a === teamId) && f.event === gwNumber
    );
    if (teamFixtures.length === 0) return 99; // blank = sort to end
    return teamFixtures.reduce((total, fixture) => {
      const home = fixture.team_h === teamId;
      if (sortBy === "original") {
        return total + (home ? fixture.team_h_difficulty : fixture.team_a_difficulty);
      } else {
        const opponentNumber = home ? fixture.team_a : fixture.team_h;
        return total + getTeamDiff(opponentNumber, home);
      }
    }, 0);
  };

  const teamsToRender = teams.slice(1);
  
  // Responsive design logic
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const isNarrowScreen = screenWidth < 1650;
  const shouldCompactOpponents = isNarrowScreen && numberOfGameweeks >= 8;
  
  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const sortedTeams = [...teamsToRender];
  sortedTeams.sort((teamA, teamB) => {
    let valueA, valueB;

    if (sortColumn === "team") {
      const nameA = teams[teamA.id]?.name || teamA.name || '';
      const nameB = teams[teamB.id]?.name || teamB.name || '';
      const cmp = nameA.localeCompare(nameB);
      return sortOrder === "asc" ? cmp : -cmp;
    } else if (sortColumn.startsWith("gw-")) {
      const gwNum = parseInt(sortColumn.split("-")[1], 10);
      valueA = getGameweekDifficulty(teamA.id, gwNum);
      valueB = getGameweekDifficulty(teamB.id, gwNum);
    } else {
      // fdr
      if (sortBy === "original") {
        valueA = calculateTotalDifficulty(teamA.id, numberOfGameweeks);
        valueB = calculateTotalDifficulty(teamB.id, numberOfGameweeks);
      } else {
        valueA = calculateCustomDifficulty(teamA.id, numberOfGameweeks);
        valueB = calculateCustomDifficulty(teamB.id, numberOfGameweeks);
      }
    }

    if (sortOrder === "asc") {
      return valueA - valueB;
    } else {
      return valueB - valueA;
    }
  });

  const label = { inputProps: { 'aria-label': 'Switch demo' } };

  const handleIncrement = () => {
    setActiveGameweek((prev) => Math.min(prev + 1, 38));
  };

  const handleDecrement = () => {
    setActiveGameweek((prev) => Math.max(prev - 1, 1));
  };

  return (
      <div>
        <div className="container-cardlist-tools">
          <div className="fdr-selector-group">
            <strong className="tool-text">FPL FDR</strong>
            <Switch className="switch" {...label} defaultChecked={sortBy === 'custom'} onChange={handleTableReorder}/>
            <strong className="tool-text">Oracle FDR</strong>
          </div>

          {sortBy === "custom" && (
            <div className="att-def-filter-group">
              <button
                className={`att-def-filter-btn ${showAttack ? 'active' : ''}`}
                onClick={() => setShowAttack(prev => !prev)}
                title="Attack: how easy to score against opponent"
              >
                ATT
              </button>
              <button
                className={`att-def-filter-btn ${showDefense ? 'active' : ''}`}
                onClick={() => setShowDefense(prev => !prev)}
                title="Defense: how dangerous opponent's attack is"
              >
                DEF
              </button>
            </div>
          )}

          <div className="input-container-cardlist">
            <label htmlFor="gameweek" className="tool-text"><strong>{isMobile ? "GW: " : "Active GW: "}</strong></label>
            <div className="input-wrapper">
                <button className="minus-button" onClick={() => handleDecrement(handleCustomGameweekChange, activeGameweek)}>-</button>
                <input
                    id="gameweek"
                    className="active-gameweek-input"
                    type="number"
                    min="1"
                    max="38"
                    value={activeGameweek !== null ? activeGameweek : ""}
                    onChange={handleCustomGameweekChange}
                />
                <button className="plus-button" onClick={() => handleIncrement(handleCustomGameweekChange, activeGameweek)}>+</button>
            </div>
          </div>

          <div className="gameweek-dropdown">
            <Dropdown handleGameweekChange={handleGameweekChange} activeGameweek={activeGameweek} />
          </div>
        </div>

      <div className={`table ${showOriginalScore ? "original-fpl" : ""}`}>
        <table className="fixtures-table with-border">
          <thead className="table-header">
            <tr>
              <th className="team-column sortable-header" onClick={() => handleColumnSort('team')}>
                Team{sortArrow('team')}
              </th>
              <th className="fdr-column sortable-header" onClick={() => handleColumnSort('fdr')}>
                FDR{sortArrow('fdr')}
              </th>
              {Array.from({length: numberOfGameweeks}, (_, index) => {
                const gameweekNumber = activeGameweek + index;
                const colKey = `gw-${gameweekNumber}`;
                return gameweekNumber <= 38 ? (
                  <th key={index} className="sortable-header" onClick={() => handleColumnSort(colKey)}>
                    {`GW ${gameweekNumber}`}{sortArrow(colKey)}
                  </th>
                ) : null;
              })}
            </tr>
          </thead>
          <tbody>
            {sortedTeams.map((team, index) => (
              <Row
                teams={teams}
                fixturesData={fixturesData}
                teamIndex={team.id}
                numberOfFixtures={numberOfGameweeks}
                activeGameweek={activeGameweek}
                showOriginalScore={sortBy === "original"}
                showCustomScore={sortBy === "custom"}
                diffMode={diffMode}
                key={index}
                isTableRow={true}
                isNarrowScreen={isNarrowScreen}
                shouldCompactOpponents={shouldCompactOpponents}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}